import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Globe, Check, Loader2 } from "lucide-react";
import { isRTL } from '@/lib/i18n';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },
  { code: 'ko', name: '한국어', flag: '🇰🇷', dir: 'ltr' },
  { code: 'tl', name: 'Tagalog', flag: '🇵🇭', dir: 'ltr' },
];

export default function LanguageSelector({ variant = "ghost", showLabel = false }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  
  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  // Handle RTL/LTR direction changes
  useEffect(() => {
    const dir = isRTL(i18n.language) ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  // Load user's saved language preference when logged in
  useEffect(() => {
    const loadUserLanguage = async () => {
      if (user) {
        try {
          const response = await api.get('/auth/me/language');
          if (response.data.language_preferences?.language) {
            const savedLang = response.data.language_preferences.language;
            if (savedLang !== i18n.language && !response.data.language_preferences.auto_detect) {
              i18n.changeLanguage(savedLang);
            }
          }
        } catch (error) {
          // Silently fail - user may not have saved preferences yet
          console.debug('No saved language preference found');
        }
      }
    };
    loadUserLanguage();
  }, [user]);

  const changeLanguage = async (langCode) => {
    // Change language locally first for immediate feedback
    i18n.changeLanguage(langCode);
    
    // Save to backend if user is logged in
    if (user) {
      setIsSaving(true);
      try {
        await api.put('/auth/me/language', { 
          language: langCode,
          auto_detect: false 
        });
      } catch (error) {
        console.error('Failed to save language preference:', error);
        // Don't show error toast - local change still works
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={showLabel ? "default" : "icon"} 
          className="gap-2" 
          data-testid="language-selector"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Globe className="w-4 h-4" />
          )}
          {showLabel && <span>{currentLang.name}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
          Select Language
        </div>
        <DropdownMenuSeparator />
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`cursor-pointer flex items-center justify-between ${
              i18n.language === lang.code ? 'bg-primary/10' : ''
            }`}
            data-testid={`lang-${lang.code}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-base">{lang.flag}</span>
              <span>{lang.name}</span>
              {lang.dir === 'rtl' && (
                <span className="text-[10px] px-1 py-0.5 bg-muted rounded text-muted-foreground">
                  RTL
                </span>
              )}
            </div>
            {i18n.language === lang.code && (
              <Check className="w-4 h-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
        {user && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-[10px] text-muted-foreground">
              Preference saved to your profile
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
